import {Directive, ElementRef, OnInit, inject} from '@angular/core';

/**
 * A directive that allows "pushing" any aria-* attributes from the element to its parent element.
 *
 * This exists as a helper when an element is modified by a third-party component that we don't have access to.
 * At the time of writing, it was Bootstrap tabs.
 *
 * Bootstrap made a structure like this:
 *
 * ```html
 *  <ul ngbNav role="tablist">
 *    <li>
 *      <a ngbNavLink role="tab" aria-active="true"></a>
 *    </li>
 *  </ul
 * ```
 *
 * However, the tab role has to be the immediate child to tablist.  So it has to be on the `<li>` tag.  But without that role, it's wrong
 * for the aria attributes to be on the `<a>` tag.
 *
 * Bootstrap let us disable the automatic roles and we could assign those manually.  But the aria-* attributes are automatic so they'd
 * end up applying to the `<a>` tag and not the `<li>` tag.
 *
 * Like this:
 *
 * ```html
 *  <ul ngbNav role="tablist">
 *    <li role="tab">
 *      <a ngbNavLink aria-active="true"></a>
 *    </li>
 *  </ul
 * ```
 *
 * So this directive exists to monitor the element and move any applied aria-attributes up one level.
 *
 * Like this:
 *
 *```html
 *  <ul ngbNav role="tablist">
 *    <li role="tab" aria-active="true">
 *      <a ngbNavLink appAriaAttrsParent></a>
 *    </li>
 *  </ul
 * ```
 *
 */
@Directive({selector: '[appAriaAttrsParent]'})
export class AriaAttrsParentDirective implements OnInit {
  private elemRef = inject(ElementRef);


  private observer;
  private parentElem: HTMLElement;

  private pushUpAriaAttributes() {

    //Find the aria attributes that are on the element and need to be pushed up to the parent
    const attrsToPush: any[] = Object.values(this.elemRef.nativeElement.attributes).filter((currentAttr: any) => currentAttr.name.startsWith('aria-'));

    //Push the aria attributes up to the parent.  (Add to parent and then remove from this element.)
    attrsToPush.forEach(attr => {
      this.parentElem.setAttribute(attr.name, attr.value);
      this.elemRef.nativeElement.attributes.removeNamedItem(attr.name);
    });

  }

  ngOnInit() {
    //Store a reference to the parent element
    this.parentElem = this.elemRef.nativeElement.parentElement;
    //Set up the observer that will tell us whenever this element's attributes are changed
    this.observer = new MutationObserver(mutations => {
      //If an aria-* attribute is what's changed, trigger the pushUp method that will push those attributes up to the parent html element
      if(!!mutations.find(mutation => mutation.attributeName.startsWith('aria-'))) {
        this.pushUpAriaAttributes();
      }
    });
    this.observer.observe(this.elemRef.nativeElement, {attributes: true, childList: false, characterData: false});
  }
}
